import moment from "moment";
import { UseGetApiSimple } from "beaktShared/services/crudeApiService";
import { validateDate, convertDate } from "beaktShared/helpers/masks";

// BKT-1508: espelha o enum EstabelecimentoContrato.Tipo da API (serializado como número)
const TIPO_CONTRATO = {
    RENOVACAO: 0,
    GERAL: 1,
};

// Funcionalidade (ListaFuncionalidades) exigida pela API para alterar contratos e Royalties (BKT-1507)
const PERMISSAO_GESTAO_FRANQUEADOR = "gestaofranqueador";

const PERMISSAO_EDITAR_DT_INAUGURACAO = "editarDataInauguracao";

// Permissões usadas pelas abas do Credenciamento, em uma única consulta por tela.
// Master (tipoPerfil 0) não passa por checagem de permissão, mesma regra do layout da Matriz.
const PERMISSOES_NEGADAS = { gestaoFranqueador: false, editarDataInauguracao: false };
const getPermissoesCredenciamento = (session) => {
    if (session.tipoPerfil === 0)
        return Promise.resolve({ gestaoFranqueador: true, editarDataInauguracao: true });

    return Promise.resolve(UseGetApiSimple("usuarios/getpermissoes/" + session.estabelecimentoId + "/" + session.id))
        .then(result => {
            // BKT-1514: falha na consulta nega por segurança, mas sinaliza para a tela avisar o usuário
            if (!result || result.status !== 200 || !Array.isArray(result.data))
                return { ...PERMISSOES_NEGADAS, falhaConsulta: true };
            const tem = (path) => result.data.some(p => p && p.path === path);
            return {
                gestaoFranqueador: tem(PERMISSAO_GESTAO_FRANQUEADOR),
                editarDataInauguracao: tem(PERMISSAO_EDITAR_DT_INAUGURACAO),
            };
        });
};

// BKT-1511: mesma regra da API (RoyaltieReajusteCalculo.RoyaltiesCadastrados) — algum valor preenchido e diferente de 0.
// Com Royalties cadastrados os valores só mudam pelo fluxo contratual / reajuste automático.
const royaltiesCadastrados = (estabelecimento) =>
    !!estabelecimento &&
    ["percentualRoyalties", "minRoyalties", "maxRoyalties", "fixoRoyalties"]
        .some(campo => estabelecimento[campo] !== null && estabelecimento[campo] !== undefined && Number(estabelecimento[campo]) !== 0);

// A regra de datas (Geral: início + 5 anos e 5 dias / Renovação: início + 1 ano e 1 dia) é da API — o front só exibe
const getSugestaoDatasContrato = (tipo, inicio) => {
    let endPoint = "estabelecimentos/contratos/sugestaodatas?tipo=" + tipo;
    if (inicio)
        endPoint += "&inicio=" + moment(inicio).format("YYYY-MM-DD");
    return UseGetApiSimple(endPoint);
};

const ehContratoGeral = (contrato) => contrato.tipoContrato === TIPO_CONTRATO.GERAL;

const separarContratos = (contratos) => {
    const lista = contratos || [];
    const ordenarPorInicio = (a, b) => new Date(a.inicioContrato) - new Date(b.inicioContrato);
    return {
        geral: lista.filter(ehContratoGeral).sort(ordenarPorInicio).pop() || null,
        renovacoes: lista.filter(c => !ehContratoGeral(c)).sort(ordenarPorInicio),
    };
};

// Ciclo de Renovação em vigor na data informada (o último ciclo iniciado, se nenhum cobrir a data)
const renovacaoVigente = (renovacoes, hoje = new Date()) => {
    const dia = moment(hoje).startOf("day");
    const iniciados = (renovacoes || []).filter(c => !moment(c.inicioContrato).startOf("day").isAfter(dia));
    const vigente = iniciados.find(c => !moment(c.terminoContrato).startOf("day").isBefore(dia));
    return vigente || iniciados[iniciados.length - 1] || null;
};

// BKT-1514: ciclo já encerrado e ainda sem o próximo (aguardando a renovação automática)
const cicloVencido = (ciclo, hoje = new Date()) =>
    !!ciclo && moment(ciclo.terminoContrato).startOf("day").isBefore(moment(hoje).startOf("day"));

const validarContratoGeral = (inicio, termino) => {
    if (!inicio || !termino)
        return "required";
    if (!moment(termino).startOf("day").isAfter(moment(inicio).startOf("day")))
        return "termino_before_start";
    return null;
};

// BKT-1513: validação do formulário do Contrato Geral (datas digitadas no formato do idioma), usada no
// cadastro da unidade e na migração. Retorna null ou { campo, mensagem } com a chave i18n da mensagem.
const validarFormContratoGeral = (form) => {
    const { inicio, termino, sugestaoPendente } = form || {};
    if (!inicio)
        return { campo: "inicio", mensagem: "accreditation.general_contract_required" };
    // BKT-1514: término ainda é a sugestão do início anterior
    if (sugestaoPendente)
        return { campo: "termino", mensagem: "accreditation.general_contract_suggestion_pending" };
    if (!termino)
        return { campo: "termino", mensagem: "accreditation.general_contract_required" };
    if (!validateDate(inicio))
        return { campo: "inicio", mensagem: "error_message.invalid_date" };
    if (!validateDate(termino))
        return { campo: "termino", mensagem: "error_message.invalid_date" };
    if (validarContratoGeral(convertDate(inicio), convertDate(termino)))
        return { campo: "termino", mensagem: "accreditation.general_contract_end_before_start" };
    return null;
};

// BKT-1513: contrato enviado na migração de unidade; a API gera o 1º ciclo de Renovação a partir dele
const montarContratoGeralMigracao = (form) => [{
    estabelecimentoContratoId: 0,
    estabelecimentoId: 0,
    tipoContrato: TIPO_CONTRATO.GERAL,
    inicioContrato: convertDate(form.inicio),
    terminoContrato: convertDate(form.termino),
}];

// Insere ou atualiza o Contrato Geral na lista enviada à API; os ciclos de Renovação seguem como estão
// (a API gera/realinha o 1º ciclo a partir do Geral).
const aplicarContratoGeral = (contratos, estabelecimentoId, inicio, termino) => {
    const { geral } = separarContratos(contratos);
    const outros = (contratos || []).filter(c => c !== geral);
    const atualizado = {
        ...(geral || {}),
        estabelecimentoId: estabelecimentoId || 0,
        estabelecimentoContratoId: geral ? geral.estabelecimentoContratoId : 0,
        tipoContrato: TIPO_CONTRATO.GERAL,
        inicioContrato: inicio,
        terminoContrato: termino,
    };
    return [atualizado, ...outros];
};

// BKT-1514: unidade já cadastrada sem Contrato Geral (legado) e sem datas digitadas: o Geral fica pendente
// e não bloqueia salvar os outros dados. Cadastro novo e migração continuam exigindo o Geral.
const contratoGeralPendente = (isEdicao, contratos, form) => {
    const { inicio, termino } = form || {};
    return !!isEdicao && !separarContratos(contratos).geral && !inicio && !termino;
};

export {
    TIPO_CONTRATO,
    contratoGeralPendente,
    PERMISSAO_GESTAO_FRANQUEADOR,
    PERMISSOES_NEGADAS,
    getPermissoesCredenciamento,
    royaltiesCadastrados,
    getSugestaoDatasContrato,
    separarContratos,
    renovacaoVigente,
    cicloVencido,
    validarContratoGeral,
    validarFormContratoGeral,
    montarContratoGeralMigracao,
    aplicarContratoGeral,
};
